import {
  DisplayEntity,
  MediaPlaylistEntity,
  OfflinePlaybackPlan as DomainOfflinePlaybackPlan,
} from "@admefy/domain";
import { inject, Injectable } from "@angular/core";
import { WA_LOCAL_STORAGE } from "@ng-web-apis/common";
import { randomString } from "../../utils/randomId";

export type OfflinePlaybackPlan = DomainOfflinePlaybackPlan<MediaPlaylistEntity>;

interface PersistedPlaybackSnapshot {
  currentIndex: number;
  nextIndex?: number;
  currentOccurrence?: MediaPlaylistEntity;
  nextOccurrence?: MediaPlaylistEntity;
}

interface PersistedOfflinePlayback {
  schemaVersion: 1;
  displayId: string;
  cursor: number;
  plan: OfflinePlaybackPlan;
  playback?: PersistedPlaybackSnapshot;
  rotationCompleted?: Record<string, number>;
  updatedAt: string;
}

type PlaybackAnchor = MediaPlaylistEntity | null | undefined;

export class OfflinePlaybackRepository {
  private readonly inMemoryRecords = new Map<string, PersistedOfflinePlayback>();
  private readonly rotationIssued = new Map<string, number>();
  private inMemoryDisplay: DisplayEntity | undefined;

  constructor(private readonly storage?: Storage | null) {}

  storageKey(displayId: string): string {
    return `admefy:offline-playback:v1:${encodeURIComponent(displayId)}`;
  }

  displayStorageKey(): string {
    return "admefy:offline-playback:v1:active-display";
  }

  saveDisplay(display: DisplayEntity): boolean {
    if (!display?.id) {
      return false;
    }

    this.inMemoryDisplay = Object.assign(new DisplayEntity(), display);

    try {
      this.storage?.setItem(this.displayStorageKey(), JSON.stringify(display));
    } catch {
      // La configuración permanece disponible en memoria durante esta sesión.
    }

    return true;
  }

  getDisplay(): DisplayEntity | undefined {
    let serialized: string | null = null;

    try {
      serialized = this.storage?.getItem(this.displayStorageKey()) ?? null;
    } catch {
      return this.inMemoryDisplay;
    }

    if (!serialized) {
      return this.inMemoryDisplay;
    }

    try {
      const display = JSON.parse(serialized) as Partial<DisplayEntity>;
      if (!display?.id || typeof display.id !== "string") {
        this.clearDisplay();
        return undefined;
      }

      this.inMemoryDisplay = Object.assign(new DisplayEntity(), display);
      return this.inMemoryDisplay;
    } catch {
      this.clearDisplay();
      return undefined;
    }
  }

  clearDisplay(): void {
    this.inMemoryDisplay = undefined;

    try {
      this.storage?.removeItem(this.displayStorageKey());
    } catch {
      // La configuración en memoria ya está limpia.
    }
  }

  savePlan(
    displayId: string,
    plan: OfflinePlaybackPlan,
    currentMedia?: PlaybackAnchor,
    nextMedia?: PlaybackAnchor,
  ): boolean {
    plan = this.withoutProgrammaticItems(plan);
    if (!this.isValidPlan(plan, true)) {
      return false;
    }

    if (plan.medias.length === 0) {
      this.clear(displayId);
      return true;
    }

    const previous = this.read(displayId);
    const playback = this.findPlaybackSnapshot(plan, currentMedia, nextMedia, previous?.cursor);
    const reconciledCursor = this.cursorAfterSnapshot(playback, plan.medias.length);
    const cursor =
      reconciledCursor ??
      (previous?.plan.loopKey === plan.loopKey
        ? this.normalizeCursor(previous.cursor, plan.medias.length)
        : 0);
    const record: PersistedOfflinePlayback = {
      schemaVersion: 1,
      displayId,
      cursor,
      plan: this.hydratePlan(plan),
      playback,
      rotationCompleted: previous?.rotationCompleted,
      updatedAt: new Date().toISOString(),
    };

    this.write(record);
    this.resetIssued(displayId);
    return true;
  }

  reconcile(displayId: string, currentMedia?: PlaybackAnchor, nextMedia?: PlaybackAnchor): boolean {
    const record = this.read(displayId);
    if (!record) {
      return false;
    }

    const playback = this.findPlaybackSnapshot(record.plan, currentMedia, nextMedia, record.cursor);
    const cursor = this.cursorAfterSnapshot(playback, record.plan.medias.length);
    if (!playback || cursor === undefined) {
      return false;
    }

    this.write({
      ...record,
      cursor,
      playback,
      updatedAt: new Date().toISOString(),
    });
    this.resetIssued(displayId);
    return true;
  }

  savePlaybackState(
    displayId: string,
    currentMedia?: PlaybackAnchor,
    nextMedia?: PlaybackAnchor,
  ): boolean {
    const record = this.read(displayId);
    if (!record || (!currentMedia && !nextMedia)) {
      return false;
    }

    const playback = this.findPlaybackSnapshot(record.plan, currentMedia, nextMedia, record.cursor);
    if (!playback) {
      return false;
    }

    const cursor = this.cursorAfterSnapshot(playback, record.plan.medias.length) ?? record.cursor;
    this.write({
      ...record,
      cursor,
      playback,
      updatedAt: new Date().toISOString(),
    });
    this.resetIssued(displayId);
    return true;
  }

  restorePlaybackState(displayId: string):
    | {
        currentMedia: MediaPlaylistEntity;
        nextMedia: MediaPlaylistEntity | undefined;
      }
    | undefined {
    const record = this.read(displayId);
    const playback = record?.playback;
    if (!record || !playback) {
      return undefined;
    }

    const current = record.plan.medias[playback.currentIndex];
    if (!current) {
      return undefined;
    }

    return {
      currentMedia: this.createLocalOccurrence(
        playback.currentOccurrence ?? this.resolveRotation(record, current),
      ),
      nextMedia:
        playback.nextIndex === undefined
          ? undefined
          : this.createLocalOccurrence(
              playback.nextOccurrence ??
                this.resolveRotation(record, record.plan.medias[playback.nextIndex]),
            ),
    };
  }

  takeNext(displayId: string): MediaPlaylistEntity | undefined {
    const record = this.read(displayId);
    if (!record?.plan.medias.length) {
      return undefined;
    }

    const cursor = this.normalizeCursor(record.cursor, record.plan.medias.length);
    const media = this.resolveRotation(record, record.plan.medias[cursor]);
    this.write({
      ...record,
      cursor: (cursor + 1) % record.plan.medias.length,
      updatedAt: new Date().toISOString(),
    });

    return this.createLocalOccurrence(media);
  }

  /** Terminal outcomes, including skips, consume a turn; preloading never commits one. */
  completeRotation(displayId: string, media: PlaybackAnchor): boolean {
    const record = this.read(displayId);
    const occurrence = media?.creativeOccurrence;
    if (
      !record ||
      !media ||
      !occurrence ||
      !Number.isSafeInteger(occurrence.ordinal) ||
      occurrence.ordinal < 0
    )
      return false;
    const source = record.plan.medias.find((item) => this.isSameOccurrence(item, media));
    if (!source?.creativeRotation || source.creativeRotation.snapshotId !== occurrence.snapshotId)
      return false;
    const selected =
      source.creativeRotation.creatives[
        occurrence.ordinal % source.creativeRotation.creatives.length
      ];
    if (selected.creativeId !== occurrence.creativeId || selected.media.id !== media.id)
      return false;
    const key = this.rotationKey(media.mediaCampaignId, occurrence.snapshotId);
    this.write({
      ...record,
      rotationCompleted: {
        ...record.rotationCompleted,
        [key]: Math.max(record.rotationCompleted?.[key] ?? -1, occurrence.ordinal),
      },
      updatedAt: new Date().toISOString(),
    });
    return true;
  }

  private rotationKey(campaignId: string, snapshotId: string): string {
    return JSON.stringify([campaignId, snapshotId]);
  }

  private resolveRotation(
    record: PersistedOfflinePlayback,
    source: MediaPlaylistEntity,
  ): MediaPlaylistEntity {
    const rotation = source.creativeRotation;
    if (!rotation) return source;
    const key = this.rotationKey(source.mediaCampaignId, rotation.snapshotId);
    const issuedKey = JSON.stringify([record.displayId, key]);
    const buffered = [record.playback?.currentOccurrence, record.playback?.nextOccurrence]
      .filter(
        (item) =>
          item?.mediaCampaignId === source.mediaCampaignId &&
          item.creativeOccurrence?.snapshotId === rotation.snapshotId,
      )
      .map((item) => item!.creativeOccurrence!.ordinal);
    const ordinal =
      Math.max(
        rotation.completedOrdinal ?? -1,
        record.rotationCompleted?.[key] ?? -1,
        this.rotationIssued.get(issuedKey) ?? -1,
        ...buffered,
      ) + 1;
    if (!Number.isSafeInteger(ordinal)) throw new Error("CAMPAIGN_ROTATION_ORDINAL_INVALID");
    const selected = rotation.creatives[ordinal % rotation.creatives.length];
    this.rotationIssued.set(issuedKey, ordinal);
    return Object.assign(new MediaPlaylistEntity(), selected.media, {
      mediaCampaignId: source.mediaCampaignId,
      playlistId: source.playlistId,
      mediaDuration: source.mediaDuration,
      hours: source.hours,
      slotType: source.slotType,
      ownerSlotIndex: source.ownerSlotIndex,
      loopKey: source.loopKey,
      objectFit: source.objectFit,
      isLastMedia: source.isLastMedia,
      creativeOccurrence: {
        snapshotId: rotation.snapshotId,
        creativeId: selected.creativeId,
        ordinal,
        ...(selected.unavailable ? { unavailable: true } : {}),
      },
    });
  }

  getPlan(displayId: string): OfflinePlaybackPlan | undefined {
    return this.read(displayId)?.plan;
  }

  getRotationProgress(
    displayId: string,
  ): { campaignId: string; snapshotId: string; ordinal: number }[] {
    const record = this.read(displayId);
    if (!record) return [];
    const unique = new Map<string, { campaignId: string; snapshotId: string; ordinal: number }>();
    for (const media of record.plan.medias) {
      if (!media.creativeRotation) continue;
      const key = this.rotationKey(media.mediaCampaignId, media.creativeRotation.snapshotId);
      const ordinal = record.rotationCompleted?.[key];
      if (ordinal !== undefined)
        unique.set(key, {
          campaignId: media.mediaCampaignId,
          snapshotId: media.creativeRotation.snapshotId,
          ordinal,
        });
    }
    return [...unique.values()];
  }

  clear(displayId: string): void {
    this.inMemoryRecords.delete(displayId);
    this.resetIssued(displayId);

    try {
      this.storage?.removeItem(this.storageKey(displayId));
    } catch {
      // El registro en memoria ya está limpio.
    }
  }

  private resetIssued(displayId: string): void {
    for (const key of this.rotationIssued.keys()) {
      if ((JSON.parse(key) as string[])[0] === displayId) this.rotationIssued.delete(key);
    }
  }

  private read(displayId: string): PersistedOfflinePlayback | undefined {
    let serialized: string | null = null;

    try {
      serialized = this.storage?.getItem(this.storageKey(displayId)) ?? null;
    } catch {
      return this.inMemoryRecords.get(displayId);
    }

    if (!serialized) {
      return this.inMemoryRecords.get(displayId);
    }

    try {
      const parsed = JSON.parse(serialized) as unknown;
      if (!this.isValidRecord(parsed, displayId)) {
        this.clear(displayId);
        return undefined;
      }

      const record = {
        ...parsed,
        plan: this.hydratePlan(parsed.plan),
      };
      this.inMemoryRecords.set(displayId, record);
      return record;
    } catch {
      this.clear(displayId);
      return undefined;
    }
  }

  private write(record: PersistedOfflinePlayback): void {
    this.inMemoryRecords.set(record.displayId, record);

    try {
      this.storage?.setItem(this.storageKey(record.displayId), JSON.stringify(record));
    } catch {
      // El plan permanece disponible en memoria durante esta sesión.
    }
  }

  private hydratePlan(plan: OfflinePlaybackPlan): OfflinePlaybackPlan {
    return {
      ...plan,
      medias: plan.medias.map((media) => Object.assign(new MediaPlaylistEntity(), media)),
    };
  }

  private findPlaybackSnapshot(
    plan: OfflinePlaybackPlan,
    currentMedia?: PlaybackAnchor,
    nextMedia?: PlaybackAnchor,
    previousCursor?: number,
  ): PersistedPlaybackSnapshot | undefined {
    if (currentMedia && nextMedia) {
      const pairIndex = plan.medias.findIndex(
        (media, index) =>
          this.isSameOccurrence(media, currentMedia) &&
          this.isSameOccurrence(plan.medias[(index + 1) % plan.medias.length], nextMedia),
      );

      if (pairIndex !== -1) {
        return {
          currentIndex: pairIndex,
          nextIndex: (pairIndex + 1) % plan.medias.length,
          ...(currentMedia.creativeOccurrence ? { currentOccurrence: currentMedia } : {}),
          ...(nextMedia.creativeOccurrence ? { nextOccurrence: nextMedia } : {}),
        };
      }
    }

    if (currentMedia) {
      const expectedCurrentIndex =
        previousCursor === undefined
          ? undefined
          : this.normalizeCursor(previousCursor - 1, plan.medias.length);
      if (
        expectedCurrentIndex !== undefined &&
        this.isSameOccurrence(plan.medias[expectedCurrentIndex], currentMedia)
      ) {
        return {
          currentIndex: expectedCurrentIndex,
          ...(currentMedia.creativeOccurrence ? { currentOccurrence: currentMedia } : {}),
        };
      }

      const currentIndex = plan.medias.findIndex((media) =>
        this.isSameOccurrence(media, currentMedia),
      );
      if (currentIndex !== -1) {
        return {
          currentIndex,
          ...(currentMedia.creativeOccurrence ? { currentOccurrence: currentMedia } : {}),
        };
      }
    }

    if (nextMedia) {
      const expectedNextIndex =
        previousCursor === undefined
          ? undefined
          : this.normalizeCursor(previousCursor - 1, plan.medias.length);
      const nextIndex =
        expectedNextIndex !== undefined &&
        this.isSameOccurrence(plan.medias[expectedNextIndex], nextMedia)
          ? expectedNextIndex
          : plan.medias.findIndex((media) => this.isSameOccurrence(media, nextMedia));
      if (nextIndex !== -1) {
        return {
          currentIndex: this.normalizeCursor(nextIndex - 1, plan.medias.length),
          nextIndex,
          ...(nextMedia.creativeOccurrence ? { nextOccurrence: nextMedia } : {}),
        };
      }
    }

    return undefined;
  }

  private cursorAfterSnapshot(
    playback: PersistedPlaybackSnapshot | undefined,
    length: number,
  ): number | undefined {
    if (!playback) {
      return undefined;
    }

    return this.normalizeCursor((playback.nextIndex ?? playback.currentIndex) + 1, length);
  }

  private createLocalOccurrence(media: MediaPlaylistEntity | undefined): MediaPlaylistEntity {
    return Object.assign(new MediaPlaylistEntity(), media, {
      identifier: randomString(),
    });
  }

  private isSameOccurrence(left: PlaybackAnchor, right: PlaybackAnchor): boolean {
    if (left?.creativeRotation && right?.creativeOccurrence) {
      const occurrence = right.creativeOccurrence;
      if (
        left.creativeRotation.snapshotId !== occurrence.snapshotId ||
        !Number.isSafeInteger(occurrence.ordinal) ||
        occurrence.ordinal < 0
      )
        return false;
      const selected =
        left.creativeRotation.creatives[
          occurrence.ordinal % left.creativeRotation.creatives.length
        ];
      if (selected?.creativeId !== occurrence.creativeId || selected.media.id !== right.id)
        return false;
    } else if (right?.creativeOccurrence) return false;
    return (
      !!left &&
      !!right &&
      (left.id === right.id ||
        (left.creativeRotation?.snapshotId === right.creativeOccurrence?.snapshotId &&
          !!left.creativeRotation?.creatives.some((item) => item.media.id === right.id))) &&
      left.mediaCampaignId === right.mediaCampaignId &&
      (left.slotType === undefined ||
        right.slotType === undefined ||
        left.slotType === right.slotType) &&
      (left.ownerSlotIndex === undefined ||
        right.ownerSlotIndex === undefined ||
        left.ownerSlotIndex === right.ownerSlotIndex)
    );
  }

  private normalizeCursor(cursor: number, length: number): number {
    return ((cursor % length) + length) % length;
  }

  private isValidRecord(
    value: unknown,
    expectedDisplayId: string,
  ): value is PersistedOfflinePlayback {
    if (!value || typeof value !== "object") {
      return false;
    }

    const record = value as Partial<PersistedOfflinePlayback>;
    return (
      record.schemaVersion === 1 &&
      record.displayId === expectedDisplayId &&
      typeof record.cursor === "number" &&
      Number.isInteger(record.cursor) &&
      !!record.plan &&
      this.isValidPlan(record.plan) &&
      (record.rotationCompleted === undefined ||
        (!!record.rotationCompleted &&
          typeof record.rotationCompleted === "object" &&
          !Array.isArray(record.rotationCompleted) &&
          Object.values(record.rotationCompleted).every(
            (ordinal) => Number.isSafeInteger(ordinal) && ordinal >= 0,
          ))) &&
      this.validSavedOccurrence(
        record.plan,
        record.playback?.currentIndex,
        record.playback?.currentOccurrence,
      ) &&
      this.validSavedOccurrence(
        record.plan,
        record.playback?.nextIndex,
        record.playback?.nextOccurrence,
      ) &&
      (record.playback === undefined ||
        (Number.isInteger(record.playback.currentIndex) &&
          record.playback.currentIndex >= 0 &&
          record.playback.currentIndex < record.plan.medias.length &&
          (record.playback.nextIndex === undefined ||
            (Number.isInteger(record.playback.nextIndex) &&
              record.playback.nextIndex >= 0 &&
              record.playback.nextIndex < record.plan.medias.length))))
    );
  }

  private isValidPlan(plan: OfflinePlaybackPlan, allowEmpty = false): boolean {
    return (
      !!plan &&
      typeof plan.generatedAt === "string" &&
      typeof plan.loopKey === "string" &&
      typeof plan.playlistId === "string" &&
      Array.isArray(plan.medias) &&
      (plan.playlistId.length > 0 || (allowEmpty && plan.medias.length === 0)) &&
      (allowEmpty || plan.medias.length > 0) &&
      plan.medias.every(
        (media) =>
          !!media &&
          typeof media.id === "string" &&
          media.id.length > 0 &&
          !this.isProgrammaticItem(media) &&
          this.validRotation(media) &&
          media.playlistId === plan.playlistId,
      )
    );
  }

  private validRotation(media: MediaPlaylistEntity): boolean {
    const rotation = media.creativeRotation;
    if (rotation === undefined) return true;
    return (
      !!rotation &&
      rotation.version === 1 &&
      typeof rotation.snapshotId === "string" &&
      rotation.snapshotId.length > 0 &&
      (rotation.completedOrdinal === undefined ||
        (Number.isSafeInteger(rotation.completedOrdinal) && rotation.completedOrdinal >= -1)) &&
      typeof media.mediaCampaignId === "string" &&
      media.mediaCampaignId.length > 0 &&
      Array.isArray(rotation.creatives) &&
      rotation.creatives.length >= 2 &&
      rotation.creatives.length <= 4 &&
      new Set(rotation.creatives.map((item) => item?.creativeId)).size ===
        rotation.creatives.length &&
      rotation.creatives.every(
        (item) =>
          !!item &&
          typeof item.creativeId === "string" &&
          item.creativeId.length > 0 &&
          !!item.media &&
          typeof item.media.id === "string" &&
          item.media.id.length > 0 &&
          ["image", "video"].includes(item.media.mediaType),
      )
    );
  }

  private validSavedOccurrence(
    plan: OfflinePlaybackPlan,
    index: number | undefined,
    media: MediaPlaylistEntity | undefined,
  ): boolean {
    if (media === undefined) return true;
    const source = index === undefined ? undefined : plan.medias[index];
    const occurrence = media?.creativeOccurrence;
    if (
      !source?.creativeRotation ||
      !occurrence ||
      !Number.isSafeInteger(occurrence.ordinal) ||
      occurrence.ordinal < 0 ||
      source.mediaCampaignId !== media.mediaCampaignId ||
      source.creativeRotation.snapshotId !== occurrence.snapshotId
    )
      return false;
    const selected =
      source.creativeRotation.creatives[
        occurrence.ordinal % source.creativeRotation.creatives.length
      ];
    return selected.creativeId === occurrence.creativeId && selected.media.id === media.id;
  }

  private withoutProgrammaticItems(plan: OfflinePlaybackPlan): OfflinePlaybackPlan {
    if (!Array.isArray(plan?.medias)) return plan;
    return {
      ...plan,
      medias: plan.medias.filter((media) => !this.isProgrammaticItem(media)),
    };
  }

  private isProgrammaticItem(media: unknown): boolean {
    if (!media || typeof media !== "object") return false;
    const candidate = media as { slotType?: unknown; programmaticDeliveryId?: unknown };
    return (
      candidate.slotType === "programmatic" || typeof candidate.programmaticDeliveryId === "string"
    );
  }
}

@Injectable({ providedIn: "root" })
export class OfflinePlaybackService {
  private readonly repository = new OfflinePlaybackRepository(inject(WA_LOCAL_STORAGE));

  savePlan(
    displayId: string,
    plan: OfflinePlaybackPlan,
    currentMedia?: PlaybackAnchor,
    nextMedia?: PlaybackAnchor,
  ): boolean {
    return this.repository.savePlan(displayId, plan, currentMedia, nextMedia);
  }

  saveDisplay(display: DisplayEntity): boolean {
    return this.repository.saveDisplay(display);
  }

  getDisplay(): DisplayEntity | undefined {
    return this.repository.getDisplay();
  }

  reconcile(displayId: string, currentMedia?: PlaybackAnchor, nextMedia?: PlaybackAnchor): boolean {
    return this.repository.reconcile(displayId, currentMedia, nextMedia);
  }

  savePlaybackState(
    displayId: string,
    currentMedia?: PlaybackAnchor,
    nextMedia?: PlaybackAnchor,
  ): boolean {
    return this.repository.savePlaybackState(displayId, currentMedia, nextMedia);
  }

  restorePlaybackState(displayId: string):
    | {
        currentMedia: MediaPlaylistEntity;
        nextMedia: MediaPlaylistEntity | undefined;
      }
    | undefined {
    return this.repository.restorePlaybackState(displayId);
  }

  takeNext(displayId: string): MediaPlaylistEntity | undefined {
    return this.repository.takeNext(displayId);
  }

  completeRotation(displayId: string, media: PlaybackAnchor): boolean {
    return this.repository.completeRotation(displayId, media);
  }

  getPlan(displayId: string): OfflinePlaybackPlan | undefined {
    return this.repository.getPlan(displayId);
  }

  getRotationProgress(): { campaignId: string; snapshotId: string; ordinal: number }[] {
    const displayId = this.repository.getDisplay()?.id;
    return displayId ? this.repository.getRotationProgress(displayId) : [];
  }

  clear(displayId: string): void {
    this.repository.clear(displayId);
    this.repository.clearDisplay();
  }
}
